// src/components/compliance/CameraCapture.tsx
/**
 * Camera capture for the GwG self identification (Issue #1070).
 *
 * One picture at a time: live view → shutter → preview → «Verwenden» or
 * «Wiederholen». The picture only leaves the component once it was accepted.
 *
 * Works with the phone camera and the desktop webcam. The back camera is the
 * default for a document, the front camera for the selfie; where more than one
 * camera is available the user may pick another one. Without a camera or
 * without permission nothing is forced — the parent is told through
 * {@link Props.onUnavailable} and offers the upload instead.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Check, Loader2, RefreshCw, VideoOff } from 'lucide-react'
import {
  KYC_CAPTURE_FALLBACK_LONG_EDGE_PX,
  KYC_CAPTURE_IDEAL_HEIGHT_PX,
  KYC_CAPTURE_IDEAL_WIDTH_PX,
  KYC_CAPTURE_JPEG_QUALITY,
  KYC_CAPTURE_TAKE_PHOTO_TIMEOUT_MS,
  KYC_SELF_SLOT,
  KYC_SELFIE_COUNTDOWN_S,
  KYC_SELFIE_COUNTDOWN_STEP_MS,
  KYC_UPLOAD_MAX_BYTES,
} from '@/constants/kyc'
import type { KycSelfSlot } from '@/types/kyc'
import {
  captureCropRect,
  documentFrameRect,
  meetsMinResolution,
  visibleSourceRect,
  type CameraCaptureKind,
  type CropRect,
} from './cameraCrop'
import {
  createScannerClient,
  isPlausibleCard,
  offsetQuad,
  QuadSmoother,
  scaleQuad,
  SCAN_BACKGROUND_HINT_MS,
  SCAN_INTERVAL_MS,
  SCAN_LONG_EDGE_PX,
  SCAN_MISS_GRACE,
  SCAN_QUAD_MAX_AGE_MS,
  StabilityTracker,
  type Quad,
  type ScanDetection,
  type ScanImage,
  type ScannerClient,
} from './documentScanner'
import {
  createFaceDetector,
  evaluateFaces,
  faceOvalRect,
  FACE_DETECT_INTERVAL_MS,
  FACE_MANUAL_FALLBACK_MS,
  FACE_OVAL_CENTER_X_SHARE,
  FACE_OVAL_HEIGHT_SHARE,
  FACE_OVAL_WIDTH_SHARE,
  FaceStability,
  HAND_GUIDE,
  type FaceDetectorLike,
  type FaceGuideState,
} from './faceGuide'

/** Size the capture falls back to while the stream has no dimensions yet. */
const FALLBACK_WIDTH = 1280
const FALLBACK_HEIGHT = 720

/** Aspect ratio of the live view before the stream reported its dimensions. */
const FALLBACK_ASPECT_RATIO = 16 / 9

/**
 * Resolution we ask the camera for — `ideal`, never `min`: a weaker camera
 * must still be allowed to deliver what it has instead of failing outright.
 */
const IDEAL_CAPTURE_SIZE = {
  width: { ideal: KYC_CAPTURE_IDEAL_WIDTH_PX },
  height: { ideal: KYC_CAPTURE_IDEAL_HEIGHT_PX },
} as const

/**
 * `ImageCapture` delivers a still picture in the sensor resolution instead of a
 * frame of the (much smaller) video stream. Chrome and Android know it, iOS
 * Safari and Firefox do not — hence the local declaration and the fallback.
 */
interface ImageCaptureLike {
  takePhoto(): Promise<Blob>
}
type ImageCaptureConstructor = new (track: MediaStreamTrack) => ImageCaptureLike

function imageCaptureConstructor(): ImageCaptureConstructor | null {
  const candidate = (globalThis as { ImageCapture?: ImageCaptureConstructor }).ImageCapture
  return typeof candidate === 'function' ? candidate : null
}

export type CameraUnavailableReason = 'denied' | 'unavailable'

/** Rejects after `ms` — a camera that never answers must not block the shutter. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err: unknown) => {
        clearTimeout(timer)
        reject(err instanceof Error ? err : new Error(String(err)))
      },
    )
  })
}

/**
 * A still picture of the camera in full resolution, or `null` where the browser
 * has no `ImageCapture`, the call fails or takes too long.
 */
async function grabStillPhoto(stream: MediaStream | null): Promise<ImageBitmap | null> {
  const ImageCaptureCtor = imageCaptureConstructor()
  const track = stream?.getVideoTracks?.()[0]
  if (!ImageCaptureCtor || !track || typeof createImageBitmap !== 'function') return null
  try {
    const blob = await withTimeout(
      new ImageCaptureCtor(track).takePhoto(),
      KYC_CAPTURE_TAKE_PHOTO_TIMEOUT_MS,
    )
    const bitmap = await withTimeout(createImageBitmap(blob), KYC_CAPTURE_TAKE_PHOTO_TIMEOUT_MS)
    if (bitmap.width > 0 && bitmap.height > 0) return bitmap
    bitmap.close?.()
    return null
  } catch {
    // No still picture — the caller stores a frame of the video stream
    return null
  }
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', KYC_CAPTURE_JPEG_QUALITY)
    } catch {
      resolve(null)
    }
  })
}

interface EncodedCapture {
  blob: Blob
  width: number
  height: number
}

/**
 * Keeps the capture below the upload limit of the API: a 4K crop stays far
 * below 15 MB, but an unusually large sensor may not — then it is scaled down.
 */
async function shrinkIfTooLarge(
  source: HTMLCanvasElement,
  encoded: EncodedCapture,
): Promise<EncodedCapture> {
  const longEdge = Math.max(encoded.width, encoded.height)
  if (encoded.blob.size <= KYC_UPLOAD_MAX_BYTES) return encoded
  if (longEdge <= KYC_CAPTURE_FALLBACK_LONG_EDGE_PX) return encoded

  const scale = KYC_CAPTURE_FALLBACK_LONG_EDGE_PX / longEdge
  const target = document.createElement('canvas')
  target.width = Math.max(1, Math.round(encoded.width * scale))
  target.height = Math.max(1, Math.round(encoded.height * scale))
  if (typeof target.toBlob !== 'function') return encoded
  try {
    target.getContext('2d')?.drawImage(source, 0, 0, target.width, target.height)
  } catch {
    return encoded
  }
  const smaller = await canvasToJpeg(target)
  return smaller ? { blob: smaller, width: target.width, height: target.height } : encoded
}

/** What the scanner is doing — drives the hint line under the live view. */
type ScannerState = 'off' | 'loading' | 'searching' | 'closer' | 'steady'

/**
 * One line per state, shown instead of the static frame text: while the
 * scanner is running the frame hint would say the same thing twice (rule R4).
 * Without a scanner the wording stays exactly as it was.
 */
const SCANNER_HINTS: Record<ScannerState, { key: string; fallback: string }> = {
  off: {
    key: 'kyc.capture.camera.frameHint',
    fallback: 'Legen Sie den Ausweis genau in den Rahmen.',
  },
  loading: { key: 'kyc.capture.scanner.loading', fallback: 'Scanner wird geladen …' },
  searching: {
    key: 'kyc.capture.scanner.searching',
    fallback: 'Halten Sie den Ausweis in den Rahmen.',
  },
  closer: { key: 'kyc.capture.scanner.closer', fallback: 'Näher heran.' },
  steady: { key: 'kyc.capture.scanner.steady', fallback: 'Ruhig halten …' },
}

/**
 * One line per state of the face guide (Issue #1119) — always naming the one
 * thing to change, never just «wrong».
 */
const FACE_HINTS: Record<FaceGuideState, { key: string; fallback: string }> = {
  none: { key: 'kyc.capture.face.none', fallback: 'Kein Gesicht erkannt.' },
  multiple: { key: 'kyc.capture.face.multiple', fallback: 'Nur eine Person im Bild.' },
  'off-center': {
    key: 'kyc.capture.face.offCenter',
    fallback: 'Halten Sie Ihr Gesicht in das Oval.',
  },
  'too-small': { key: 'kyc.capture.face.tooSmall', fallback: 'Etwas näher heran.' },
  'too-large': { key: 'kyc.capture.face.tooLarge', fallback: 'Etwas weiter weg.' },
  ok: { key: 'kyc.capture.face.steady', fallback: 'Stillhalten …' },
}

/**
 * The selfie guides, in percent of the live view (Issues #1119, #1150).
 *
 * One single overlay carries both shapes: the darkened frame has to leave the
 * face *and* the hand free, and two separate box shadows would each darken the
 * other one's cut-out. Its `viewBox` is therefore 100 × 100 with
 * `preserveAspectRatio="none"` — one unit is one percent of the box, whatever
 * height the column hands down.
 */
const GUIDE_VIEW_SIZE = 100

/**
 * The oval, derived from the very same shares the evaluation uses — the oval on
 * screen and the one the detection judges against must never drift apart.
 */
const FACE_OVAL_ELLIPSE = {
  cx: FACE_OVAL_CENTER_X_SHARE * GUIDE_VIEW_SIZE,
  cy: GUIDE_VIEW_SIZE / 2,
  rx: (FACE_OVAL_WIDTH_SHARE / 2) * GUIDE_VIEW_SIZE,
  ry: (FACE_OVAL_HEIGHT_SHARE / 2) * GUIDE_VIEW_SIZE,
} as const

/** The box the hand outline below is drawn in — upright, like a hand. */
const HAND_PATH_BOX = { width: 96, height: 140 } as const

/**
 * A raised open hand: four fingers up, the thumb to the left (Issue #1150).
 *
 * Written once and drawn twice — as the hole in the darkened frame and as the
 * white line on top — so the cut-out and the outline can never drift apart.
 */
const HAND_PATH = [
  'M 26 66',
  'L 26 26',
  'C 26 17 40 17 40 26',
  'L 40 62',
  'L 42 68',
  'L 44 60',
  'L 44 16',
  'C 44 7 58 7 58 16',
  'L 58 62',
  'L 60 66',
  'L 62 60',
  'L 62 26',
  'C 62 17 76 17 76 26',
  'L 76 62',
  'L 78 70',
  'L 80 62',
  'L 80 42',
  'C 80 34 92 34 92 42',
  'L 92 100',
  'C 92 120 80 130 60 130',
  'C 40 130 28 122 26 110',
  'C 18 108 8 102 5 94',
  'C 3 90 8 86 12 88',
  'C 18 91 24 90 26 84',
  'Z',
].join(' ')

/** Where the hand outline sits inside the overlay, as required by {@link HAND_GUIDE}. */
const HAND_RECT = {
  x: (HAND_GUIDE.centerXShare - HAND_GUIDE.widthShare / 2) * GUIDE_VIEW_SIZE,
  y: (HAND_GUIDE.centerYShare - HAND_GUIDE.heightShare / 2) * GUIDE_VIEW_SIZE,
  width: HAND_GUIDE.widthShare * GUIDE_VIEW_SIZE,
  height: HAND_GUIDE.heightShare * GUIDE_VIEW_SIZE,
} as const

/** The hand's own box, put into its place in the overlay. */
const HAND_TRANSFORM = `translate(${HAND_RECT.x} ${HAND_RECT.y}) scale(${
  HAND_RECT.width / HAND_PATH_BOX.width
} ${HAND_RECT.height / HAND_PATH_BOX.height})`

/** The same place again for the finger number, which is text and not a path. */
const HAND_LABEL_STYLE = {
  left: `${HAND_RECT.x}%`,
  top: `${HAND_RECT.y}%`,
  width: `${HAND_RECT.width}%`,
  height: `${HAND_RECT.height}%`,
} as const

/**
 * The front camera is shown as a mirror image (Issue #1151).
 *
 * Browsers hand over the raw sensor picture: the head moves to the right on
 * screen while it moves to the left in front of the phone, and the oval is next
 * to impossible to hit. Every selfie app mirrors, so this one does too.
 *
 * Display only — on the `<video>` and on the preview, never on the overlays
 * (writing would read backwards) and never on the stored capture: the reviewer
 * compares that face with the one on the ID (api#1045) and needs the real side.
 */
const MIRROR_CLASS = '-scale-x-100'

/**
 * A detected face in the coordinates of the mirrored live view (Issue #1151).
 *
 * The detector measures in the raw camera picture, the customer aims at the
 * mirrored one. The oval sits on the left of the screen — in the raw picture
 * therefore on the right — so the face is flipped inside the visible cut-out
 * before {@link evaluateFaces} judges it. Without this the guidance would send
 * people away from the very spot they are supposed to reach.
 */
function mirrorFaceInCutout(face: CropRect, visible: CropRect): CropRect {
  return {
    ...face,
    x: visible.width - (face.x - visible.x) - face.width,
    y: face.y - visible.y,
  }
}

/** The outline found in the live view, in coordinates of the scanned frame. */
interface ScanOutline {
  quad: Quad
  frameWidth: number
  frameHeight: number
  plausible: boolean
}

/** The last card that looked right, with the moment it was measured. */
interface DetectedCard {
  quad: Quad
  frameWidth: number
  frameHeight: number
  at: number
}

/**
 * The visible cut-out of the live view as a small bitmap for the worker.
 *
 * `createImageBitmap` cuts and scales in one go, off the main thread, and the
 * result is transferable — so a scan frame costs the page next to nothing
 * (Issue #1141). `object-cover` shows only the middle of the stream, hence the
 * cut-out; the bitmap keeps the shape of the live view, so its coordinates map
 * straight onto the frame box and serve the outline overlay unchanged.
 */
async function scanFrameBitmap(
  video: HTMLVideoElement,
  visible: CropRect,
): Promise<ImageBitmap | null> {
  const boxWidth = video.clientWidth
  const boxHeight = video.clientHeight
  if (!(boxWidth > 0 && boxHeight > 0)) return null

  const scale = Math.min(1, SCAN_LONG_EDGE_PX / Math.max(boxWidth, boxHeight))
  try {
    return await createImageBitmap(video, visible.x, visible.y, visible.width, visible.height, {
      resizeWidth: Math.max(1, Math.round(boxWidth * scale)),
      resizeHeight: Math.max(1, Math.round(boxHeight * scale)),
      resizeQuality: 'low',
    })
  } catch {
    // Unreadable frame — this tick is skipped, the next one tries again
    return null
  }
}

/** The pixels the worker sent back, on a canvas the capture can encode. */
function scanImageToCanvas(image: ScanImage): HTMLCanvasElement | null {
  if (!(image.width > 0 && image.height > 0)) return null

  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height
  try {
    const context = canvas.getContext('2d')
    if (!context) return null
    const target = context.createImageData(image.width, image.height)
    target.data.set(image.data)
    context.putImageData(target, 0, 0)
  } catch {
    return null
  }
  return canvas
}

/**
 * The capture with the detected card straightened onto ID-1 (Issue #1118), or
 * `null` — then the caller stores the rectangular crop as before.
 *
 * The outline was measured on the small scan frame, which shows exactly the
 * visible cut-out of the live view. It therefore travels into the still
 * picture in two steps: scaled to the size of that cut-out, then shifted by
 * its position. An outline older than {@link SCAN_QUAD_MAX_AGE_MS} is dropped
 * — the card may have moved since, and a stale outline would cut into it.
 *
 * The age is measured against `pressedAt`, the moment the shutter was released,
 * not against «now»: taking the still picture itself costs 300–900 ms on a
 * phone, and that waiting time says nothing about how fresh the outline is.
 */
async function straightenDetectedCard(
  client: ScannerClient | null,
  card: DetectedCard | null,
  bitmap: ImageBitmap | null,
  video: HTMLVideoElement,
  sourceWidth: number,
  sourceHeight: number,
  pressedAt: number,
): Promise<HTMLCanvasElement | null> {
  if (!client || !card) return null
  if (pressedAt - card.at > SCAN_QUAD_MAX_AGE_MS) return null

  const visible = visibleSourceRect(sourceWidth, sourceHeight, video.clientWidth, video.clientHeight)
  if (!visible) return null

  // Without `ImageCapture` the still picture is a frame of the video stream;
  // the worker only reads pixels, so it has to become a bitmap first.
  const source = bitmap ?? (await stillFromVideo(video))
  if (!source) return null

  const scaled = scaleQuad(
    card.quad,
    card.frameWidth,
    card.frameHeight,
    visible.width,
    visible.height,
  )
  try {
    const warped = await client.warp(source, offsetQuad(scaled, visible.x, visible.y))
    return warped ? scanImageToCanvas(warped) : null
  } finally {
    // Only the bitmap made here belongs to us; the caller closes its own
    if (!bitmap) source.close?.()
  }
}

/** A frame of the live view as a bitmap — the fallback without `ImageCapture`. */
async function stillFromVideo(video: HTMLVideoElement): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== 'function') return null
  try {
    return await createImageBitmap(video)
  } catch {
    return null
  }
}

interface Props {
  /** Which slot is being captured — part of the file name and the test ids. */
  slot: KycSelfSlot
  /** Preferred camera: `environment` for the document, `user` for the selfie. */
  facingMode: 'user' | 'environment'
  /** What is to be captured, shown above the live view. */
  label: string
  /** Extra instruction, e.g. the liveness task. */
  hint?: string
  /**
   * How many fingers the selfie has to show (Issue #1144).
   *
   * Set, the automatic release waits for a visible countdown instead of firing
   * the moment the face sits still — otherwise the picture is taken before the
   * hand is up. Unset (or on a document), everything stays as it was.
   */
  livenessFingerCount?: number | null
  /** The accepted picture — awaited so the preview stays until it is stored. */
  onCapture: (file: Blob, filename: string) => void | Promise<void>
  /** Camera missing or refused; the parent switches to the upload. */
  onUnavailable?: (reason: CameraUnavailableReason) => void
  /** Blocks the buttons while the parent uploads. */
  busy?: boolean
}

/** Object URL of a blob; jsdom and locked-down browsers may not have the API. */
function toObjectUrl(blob: Blob): string {
  try {
    return URL.createObjectURL(blob)
  } catch {
    return ''
  }
}

function releaseObjectUrl(url: string): void {
  if (!url) return
  try {
    URL.revokeObjectURL(url)
  } catch {
    // Nothing to release — the preview was rendered without an object URL
  }
}

export default function CameraCapture({
  slot,
  facingMode,
  label,
  hint,
  livenessFingerCount,
  onCapture,
  onUnavailable,
  busy = false,
}: Props) {
  const { t } = useTranslation()

  // Written by the callback ref below, so it has to stay writable
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [deviceId, setDeviceId] = useState('')
  const [error, setError] = useState<CameraUnavailableReason | null>(null)
  const [preview, setPreview] = useState<{
    blob: Blob
    url: string
    width: number
    height: number
    /** The card was straightened onto ID-1 instead of cut rectangularly. */
    straightened: boolean
  } | null>(null)
  const [starting, setStarting] = useState(true)
  /** Runs while the still picture is being taken — `takePhoto` needs a moment. */
  const [shooting, setShooting] = useState(false)
  /** CSS size of the live view — the frame overlay is derived from it. */
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 })
  /**
   * Aspect ratio of the stream; the live view takes it over so a phone in
   * portrait shows the whole sensor instead of a 16:9 strip of it (Issue #1116).
   */
  const [streamAspect, setStreamAspect] = useState<number | null>(null)

  /** Scanner mode (Issue #1117) — the hint line and the outline overlay. */
  const [scannerState, setScannerState] = useState<ScannerState>('off')
  const [outline, setOutline] = useState<ScanOutline | null>(null)
  /** The tip about the surface, shown after a long search (Issue #1163). */
  const [backgroundHint, setBackgroundHint] = useState(false)

  /**
   * Face guide of the selfie (Issue #1119).
   *
   * `faceState` is `null` while no detection has run yet, `faceDetectorReady`
   * is `null` as long as it is unknown whether there will be a detector at all.
   */
  const [faceState, setFaceState] = useState<FaceGuideState | null>(null)
  const [faceDetectorReady, setFaceDetectorReady] = useState<boolean | null>(null)
  /** Set after {@link FACE_MANUAL_FALLBACK_MS} — frees the shutter button. */
  const [faceFallback, setFaceFallback] = useState(false)
  /**
   * Seconds left of the finger countdown (Issue #1144), `null` while none runs.
   *
   * Counts 3 → 2 → 1 and releases at zero. Set once the face has been sitting
   * still in the oval, cleared as soon as it leaves it again.
   */
  const [countdown, setCountdown] = useState<number | null>(null)

  const faceDetectorRef = useRef<FaceDetectorLike | null>(null)
  const faceLoadStartedRef = useRef(false)
  const faceStabilityRef = useRef<FaceStability | null>(null)
  /** A detector arriving after the step was left has to be closed, not kept. */
  const mountedRef = useRef(true)

  /** The scanner worker, started with the first usable frame (Issue #1141). */
  const clientRef = useRef<ScannerClient | null>(null)
  /** `true` only once OpenCV answers from the worker; `false` keeps it off. */
  const scannerReadyRef = useRef(false)
  const trackerRef = useRef<StabilityTracker | null>(null)
  /**
   * Smoothing of the shown outline (Issue #1146).
   *
   * Its own smoother instead of the one inside the tracker: it is fed every
   * detection, also the ones that are not plausible yet, so the outline keeps
   * gliding while the customer is still moving the card into the frame.
   */
  const smootherRef = useRef<QuadSmoother | null>(null)
  /** Guards against a second automatic release while the picture is taken. */
  const autoFiredRef = useRef(false)
  /**
   * Last plausible card, in coordinates of the scanned frame plus its size.
   *
   * The shutter straightens exactly this outline (Issue #1118) as long as it
   * is fresh; otherwise the capture is cut rectangularly as before.
   */
  const lastQuadRef = useRef<DetectedCard | null>(null)

  /** A selfie keeps the whole visible cut-out, everything else gets the frame. */
  const captureKind: CameraCaptureKind =
    slot === KYC_SELF_SLOT.selfie ? 'selfie' : 'document'

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  /**
   * Hands the running stream to whichever `<video>` is on screen (Issue #1149).
   *
   * The preview takes the live view off the page, so «Wiederholen» mounts a
   * brand new `<video>` — and the start effect, which only runs on a camera
   * change, never touches it again. As a callback ref this runs on every mount
   * instead, so the second attempt shows the camera and not a black box.
   */
  const attachVideo = useCallback((video: HTMLVideoElement | null) => {
    videoRef.current = video
    const stream = streamRef.current
    if (!video || !stream || video.srcObject === stream) return
    video.srcObject = stream
    try {
      const played = video.play?.()
      if (played && typeof played.catch === 'function') played.catch(() => {})
    } catch {
      // Autoplay refused — the user still sees the picture after a tap
    }
  }, [])

  // Start (and restart on a camera change) — the previous stream is always
  // stopped first, otherwise the camera light stays on.
  useEffect(() => {
    const media = navigator.mediaDevices
    if (!media?.getUserMedia) {
      setStarting(false)
      setError('unavailable')
      onUnavailable?.('unavailable')
      return
    }

    let cancelled = false
    setStarting(true)
    setError(null)
    setStreamAspect(null)

    media
      .getUserMedia({
        video: deviceId
          ? { deviceId: { exact: deviceId }, ...IDEAL_CAPTURE_SIZE }
          : { facingMode, ...IDEAL_CAPTURE_SIZE },
      })
      .then(async (stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
        setStarting(false)
        // The element is already mounted here — the ref callback ran before the
        // permission came back, so the stream is handed over now.
        attachVideo(videoRef.current)
        // Device labels only become readable once permission was granted
        try {
          const all = await media.enumerateDevices?.()
          if (!cancelled && Array.isArray(all)) {
            setDevices(all.filter((device) => device.kind === 'videoinput'))
          }
        } catch {
          // Without the list there is simply no camera selection
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const name = (err as { name?: string })?.name
        const reason: CameraUnavailableReason =
          name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable'
        setStarting(false)
        setError(reason)
        onUnavailable?.(reason)
      })

    return () => {
      cancelled = true
      stopStream()
    }
    // `onUnavailable` is deliberately not a dependency — a new callback
    // identity from the parent must not restart the camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode, deviceId, stopStream, attachVideo])

  // Release the preview URL with the component
  useEffect(() => () => releaseObjectUrl(preview?.url ?? ''), [preview?.url])

  // Keep the CSS size of the live view — the overlay is positioned from it.
  // It changes with the viewport, and only exists while the live view is shown.
  useEffect(() => {
    const video = videoRef.current
    if (!video) {
      setBoxSize((previous) =>
        previous.width === 0 && previous.height === 0 ? previous : { width: 0, height: 0 },
      )
      return
    }
    const measure = () => {
      const width = video.clientWidth
      const height = video.clientHeight
      setBoxSize((previous) =>
        previous.width === width && previous.height === height ? previous : { width, height },
      )
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(video)
    return () => observer.disconnect()
  }, [preview, error, starting])

  const frame = captureKind === 'document' ? documentFrameRect(boxSize.width, boxSize.height) : null
  const scannerHint = t(SCANNER_HINTS[scannerState].key, SCANNER_HINTS[scannerState].fallback)

  const isSelfie = captureKind === 'selfie'
  /**
   * Whether the guide decides when the shutter may fire.
   *
   * It does as long as a detector is expected or running — but never without a
   * detector, and never after {@link FACE_MANUAL_FALLBACK_MS} without a single
   * good frame: nobody may end up unable to finish the identification.
   */
  const faceGuideActive = isSelfie && faceDetectorReady !== false && !faceFallback

  /**
   * The finger task, as far as it can be carried out (Issue #1144).
   *
   * Only a whole, positive number is a task somebody can follow — anything else
   * counts as «no task» and the release stays as immediate as it was.
   */
  const fingerTask =
    isSelfie &&
    typeof livenessFingerCount === 'number' &&
    Number.isInteger(livenessFingerCount) &&
    livenessFingerCount > 0
      ? livenessFingerCount
      : null

  // While the countdown runs it is the one message on the picture (rule R4) —
  // «Stillhalten …» next to «Zeigen Sie jetzt n Finger» would only compete.
  const stateHint =
    !isSelfie || faceDetectorReady === false || countdown !== null
      ? ''
      : faceState === null
        ? t('kyc.capture.face.loading', 'Gesichtserkennung wird vorbereitet …')
        : t(FACE_HINTS[faceState].key, FACE_HINTS[faceState].fallback)

  /**
   * With a finger task two things have to be placed (Issue #1150).
   *
   * Wherever the guidance would only say «into the oval» — and where there is no
   * detector to say anything at all — the one line names the hand as well;
   * whoever sees the outline has to be told what belongs into it.
   */
  const needsHandHint =
    isSelfie &&
    fingerTask !== null &&
    countdown === null &&
    (faceDetectorReady === false || faceState === 'off-center')
  const faceHint = needsHandHint
    ? t('kyc.capture.camera.selfieGuideHint', 'Gesicht ins Oval, Hand in den Umriss.')
    : stateHint

  /** The stream dimensions are only known once the metadata arrived. */
  const handleLoadedMetadata = () => {
    const video = videoRef.current
    if (!video) return
    const { videoWidth, videoHeight } = video
    setStreamAspect(videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : null)
  }

  const handleShutter = useCallback(async () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || typeof canvas.toBlob !== 'function') return

    setShooting(true)
    // The moment of the release — the outline has to be fresh in relation to it,
    // not in relation to the end of the (slow) still picture.
    const pressedAt = Date.now()
    // The still picture has the sensor resolution; without `ImageCapture` a
    // frame of the (smaller) video stream is used, as before.
    const bitmap = await grabStillPhoto(streamRef.current)
    try {
      const source: CanvasImageSource = bitmap ?? video
      const sourceWidth = bitmap ? bitmap.width : video.videoWidth
      const sourceHeight = bitmap ? bitmap.height : video.videoHeight

      // A card the scanner has just measured is pulled straight (Issue #1118)
      const straightened = await straightenDetectedCard(
        clientRef.current,
        lastQuadRef.current,
        bitmap,
        video,
        sourceWidth,
        sourceHeight,
        pressedAt,
      )

      if (!straightened) {
        // Store exactly what the customer saw — `object-cover` crops the live view
        const crop = captureCropRect(
          sourceWidth,
          sourceHeight,
          video.clientWidth,
          video.clientHeight,
          captureKind,
        )
        canvas.width = crop ? crop.width : sourceWidth || FALLBACK_WIDTH
        canvas.height = crop ? crop.height : sourceHeight || FALLBACK_HEIGHT
        try {
          const context = canvas.getContext('2d')
          if (crop) {
            context?.drawImage(
              source,
              crop.x,
              crop.y,
              crop.width,
              crop.height,
              0,
              0,
              crop.width,
              crop.height,
            )
          } else {
            context?.drawImage(source, 0, 0, canvas.width, canvas.height)
          }
        } catch {
          // No 2D context — the capture is handed over as the browser made it
        }
      }

      const output = straightened ?? canvas
      const blob = await canvasToJpeg(output)
      if (!blob) return
      const encoded = await shrinkIfTooLarge(output, {
        blob,
        width: output.width,
        height: output.height,
      })
      setPreview((previous) => {
        releaseObjectUrl(previous?.url ?? '')
        return {
          blob: encoded.blob,
          url: toObjectUrl(encoded.blob),
          width: encoded.width,
          height: encoded.height,
          straightened: straightened !== null,
        }
      })
    } finally {
      bitmap?.close?.()
      setShooting(false)
    }
  }, [captureKind])

  /**
   * Scanner mode (Issues #1117, #1141): find the card in the live view and
   * release the shutter by itself once it lies still and fills the frame.
   *
   * Every picture is looked at in the worker, never here — the page has to stay
   * responsive while OpenCV loads and while it works. At most one frame is with
   * the worker at a time: a queue would only pile up outlines that are already
   * out of date by the time they arrive.
   *
   * Documents only — a selfie is never scanned. The loop runs while the live
   * view is on screen and stops on the preview, on a camera change, while the
   * tab is hidden and with the component.
   */
  useEffect(() => {
    if (captureKind !== 'document') return
    if (starting || error || preview || autoFiredRef.current) return

    let cancelled = false
    let frameId = 0
    let lastTick = 0
    /** A frame is still with the worker — a second would only queue up. */
    let running = false

    /** Pictures in a row without a usable card — buffered (Issue #1146). */
    let misses = 0

    const judge = (detection: ScanDetection | null, now: number) => {
      if (cancelled) return

      const frame = detection ? documentFrameRect(detection.width, detection.height) : null
      const smoother = (smootherRef.current ??= new QuadSmoother())
      // The tolerance and the smoothing live in the coordinates of the scanned
      // frame, so a resize starts both over instead of mixing two scales
      if (frame && trackerRef.current?.frameWidth !== frame.width) {
        trackerRef.current = new StabilityTracker(frame.width)
        smoother.reset()
      }
      const plausible = Boolean(detection && frame && isPlausibleCard(detection.quad, frame))

      if (!detection || !frame || !plausible) {
        // A single bad picture means nothing on a hand-held phone: the outline
        // stays where it was and the steady clock keeps running until
        // SCAN_MISS_GRACE pictures in a row missed the card (Issue #1146).
        misses += 1
        if (misses < SCAN_MISS_GRACE) return

        trackerRef.current?.reset()
        if (detection && frame) {
          // The card is there, only too small — the outline stays as the hint
          // where it was seen, so «closer» has something to refer to
          setOutline({
            quad: smoother.push(detection.quad),
            frameWidth: detection.width,
            frameHeight: detection.height,
            plausible: false,
          })
          setScannerState('closer')
          return
        }
        smoother.reset()
        setOutline(null)
        setScannerState('searching')
        return
      }

      misses = 0
      // Shown and captured is the smoothed outline, not the raw detection —
      // single measurements differ by a few pixels and make it jump (#1146)
      const smoothed = smoother.push(detection.quad)
      setOutline({
        quad: smoothed,
        frameWidth: detection.width,
        frameHeight: detection.height,
        plausible: true,
      })
      lastQuadRef.current = {
        quad: smoothed,
        frameWidth: detection.width,
        frameHeight: detection.height,
        at: Date.now(),
      }

      if (!trackerRef.current?.push(detection.quad, now)) {
        setScannerState('steady')
        return
      }

      // Steady in the frame: release once, exactly like the button does
      cancelled = true
      cancelAnimationFrame(frameId)
      autoFiredRef.current = true
      void handleShutter()
    }

    const scan = (now: number) => {
      if (cancelled) return
      frameId = requestAnimationFrame(scan)
      if (now - lastTick < SCAN_INTERVAL_MS) return
      lastTick = now

      const video = videoRef.current
      if (!video || document.hidden || running) return
      // Without a transferable frame the worker has nothing to look at
      if (typeof createImageBitmap !== 'function') return
      // Without dimensions there is nothing to look at — and nothing to start
      const visible = visibleSourceRect(
        video.videoWidth,
        video.videoHeight,
        video.clientWidth,
        video.clientHeight,
      )
      if (!visible) return

      if (!clientRef.current) {
        const started = createScannerClient()
        clientRef.current = started
        setScannerState('loading')
        void started.ready.then((usable) => {
          scannerReadyRef.current = usable
          setScannerState(usable ? 'searching' : 'off')
        })
      }
      const client = clientRef.current
      if (!scannerReadyRef.current) return

      running = true
      void scanFrameBitmap(video, visible)
        .then((frame) => (frame ? client.detect(frame) : null))
        .then(
          (detection) => {
            running = false
            judge(detection, now)
          },
          () => {
            running = false
          },
        )
    }

    frameId = requestAnimationFrame(scan)
    return () => {
      cancelled = true
      cancelAnimationFrame(frameId)
    }
  }, [captureKind, starting, error, preview, deviceId, handleShutter])

  /** The scanner is looking for the card but has not got hold of it yet. */
  const scannerSearching = scannerState === 'searching' || scannerState === 'closer'

  /**
   * The tip about the surface after a long search (Issue #1163).
   *
   * An ID card on a white table hardly stands out from it, and the live hint
   * keeps saying «Halten Sie den Ausweis in den Rahmen.» without ever naming
   * the reason. After {@link SCAN_BACKGROUND_HINT_MS} of searching the one
   * sentence that helps appears — and the shutter button next to it was usable
   * the whole time anyway.
   *
   * «Searching» and «closer» count as the same wait: the card wandering
   * between the two must not keep restarting the clock. A stable detection
   * ends it, the preview and the next attempt start it over.
   */
  useEffect(() => {
    if (preview || !scannerSearching) {
      setBackgroundHint(false)
      return
    }
    const timer = setTimeout(() => setBackgroundHint(true), SCAN_BACKGROUND_HINT_MS)
    return () => clearTimeout(timer)
  }, [scannerSearching, preview])

  /** The worker lives as long as the step — a new one would load OpenCV again. */
  useEffect(
    () => () => {
      clientRef.current?.dispose()
      clientRef.current = null
      scannerReadyRef.current = false
    },
    [],
  )

  /**
   * Frees the shutter button after {@link FACE_MANUAL_FALLBACK_MS} (Issue #1119).
   *
   * Runs from the moment the selfie step opens and is deliberately never reset:
   * whoever has been standing in front of the oval for a quarter of a minute has
   * earned the plain button, also for the second attempt.
   */
  useEffect(() => {
    if (captureKind !== 'selfie') return
    const timer = setTimeout(() => setFaceFallback(true), FACE_MANUAL_FALLBACK_MS)
    return () => clearTimeout(timer)
  }, [captureKind])

  /** The detector lives as long as the step — a new one would download again. */
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      faceDetectorRef.current?.close()
      faceDetectorRef.current = null
    }
  }, [])

  /**
   * Face guide of the selfie (Issue #1119): watch the live view and release the
   * shutter by itself as soon as exactly one face sits still in the oval.
   *
   * Selfies only — an ID card is never looked for a face. The loop runs while
   * the live view is on screen and stops on the preview, on a camera change,
   * while the tab is hidden and with the component.
   */
  useEffect(() => {
    if (captureKind !== 'selfie') return
    if (starting || error || preview || autoFiredRef.current) return

    let cancelled = false
    let frameId = 0
    let lastTick = 0
    /** A frame is still being detected — a second call would queue up behind it. */
    let running = false

    if (!faceLoadStartedRef.current) {
      faceLoadStartedRef.current = true
      void createFaceDetector().then((detector) => {
        // The model may arrive after the step was left — then it is only freed
        if (!mountedRef.current) {
          detector?.close()
          return
        }
        faceDetectorRef.current = detector
        setFaceDetectorReady(detector !== null)
        if (detector) setFaceState('none')
      })
    }

    const look = (now: number) => {
      if (cancelled) return
      // The release is already under way (countdown or button) — nothing left
      // to watch, and a second one would overwrite the picture.
      if (autoFiredRef.current) {
        cancelled = true
        return
      }
      frameId = requestAnimationFrame(look)
      if (now - lastTick < FACE_DETECT_INTERVAL_MS) return
      lastTick = now

      const detector = faceDetectorRef.current
      const video = videoRef.current
      if (!detector || !video || document.hidden || running) return

      // `object-cover` shows only the middle of the stream — the oval and the
      // faces have to be measured in exactly that cut-out.
      const visible = visibleSourceRect(
        video.videoWidth,
        video.videoHeight,
        video.clientWidth,
        video.clientHeight,
      )
      const oval = visible ? faceOvalRect(visible.width, visible.height) : null
      if (!visible || !oval) return

      running = true
      void detector.detect(video, now).then(
        (faces) => {
          running = false
          if (cancelled || autoFiredRef.current) return

          // This loop is the selfie's alone, and the selfie is mirrored — the
          // faces are judged in exactly the picture the customer sees (#1151)
          const state = evaluateFaces(
            faces.map((face) => mirrorFaceInCutout(face, visible)),
            oval,
          )
          setFaceState(state)

          faceStabilityRef.current ??= new FaceStability()
          const steady = faceStabilityRef.current.push(state, now)

          // A steady face says nothing about the fingers (Issue #1144): with a
          // finger task the still face only starts the countdown, and the
          // release happens when it runs out. Leaves the face the oval in
          // between, the seconds are thrown away and start over.
          if (fingerTask !== null) {
            if (state !== 'ok') setCountdown(null)
            else if (steady) setCountdown((previous) => previous ?? KYC_SELFIE_COUNTDOWN_S)
            return
          }

          if (!steady) return

          // Steady in the oval: release once, exactly like the button does
          cancelled = true
          cancelAnimationFrame(frameId)
          autoFiredRef.current = true
          void handleShutter()
        },
        () => {
          running = false
        },
      )
    }

    frameId = requestAnimationFrame(look)
    return () => {
      cancelled = true
      cancelAnimationFrame(frameId)
    }
  }, [captureKind, starting, error, preview, deviceId, fingerTask, handleShutter])

  /**
   * The seconds of the finger countdown (Issue #1144): one step per second, the
   * release at zero.
   *
   * No step is scheduled while the picture is being taken or while the preview
   * is up — the release has happened by then, a second one would overwrite it.
   * Which second is on screen is state, so a cancelled countdown simply takes
   * its timer with it.
   */
  useEffect(() => {
    if (countdown === null || shooting || preview) return

    const timer = setTimeout(() => {
      if (countdown > 1) {
        setCountdown(countdown - 1)
        return
      }
      setCountdown(null)
      if (autoFiredRef.current) return
      autoFiredRef.current = true
      void handleShutter()
    }, KYC_SELFIE_COUNTDOWN_STEP_MS)
    return () => clearTimeout(timer)
  }, [countdown, shooting, preview, handleShutter])

  /** Back to the live view: the scanner starts over for the next attempt. */
  const resetScanner = useCallback(() => {
    autoFiredRef.current = false
    setCountdown(null)
    setBackgroundHint(false)
    trackerRef.current = null
    smootherRef.current?.reset()
    lastQuadRef.current = null
    setOutline(null)
    setScannerState(scannerReadyRef.current ? 'searching' : 'off')
    faceStabilityRef.current?.reset()
    setFaceState(faceDetectorRef.current ? 'none' : null)
  }, [])

  const handleRetake = () => {
    resetScanner()
    setPreview((previous) => {
      releaseObjectUrl(previous?.url ?? '')
      return null
    })
  }

  const handleUse = async () => {
    if (!preview) return
    await onCapture(preview.blob, `${slot}.jpg`)
    resetScanner()
    setPreview((previous) => {
      releaseObjectUrl(previous?.url ?? '')
      return null
    })
  }

  /**
   * The live step is a column that lives off the height it is given (Issue
   * #1143): everything but the picture keeps its size, the picture takes what
   * is left. No guessed «everything else» any more — the browser measures it.
   */
  const live = !error && !preview

  return (
    <div
      data-testid="kyc-capture-camera"
      className={live ? 'flex min-h-0 flex-auto flex-col gap-3' : 'space-y-3'}
    >
      <div className="shrink-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {hint && <p className="text-sm text-gray-500 mt-0.5">{hint}</p>}
      </div>

      {error ? (
        <div
          data-testid="kyc-capture-camera-error"
          role="status"
          className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
        >
          <VideoOff size={18} className="flex-shrink-0 mt-0.5" />
          <p>
            {error === 'denied'
              ? t(
                  'kyc.capture.camera.denied',
                  'Wir dürfen nicht auf Ihre Kamera zugreifen. Erlauben Sie den Zugriff im Browser oder laden Sie stattdessen Fotos hoch.',
                )
              : t(
                  'kyc.capture.camera.unavailable',
                  'Wir finden keine Kamera auf diesem Gerät. Laden Sie stattdessen Fotos oder eine PDF-Kopie hoch.',
                )}
          </p>
        </div>
      ) : preview ? (
        <div className="space-y-3">
          {/* The capture is only stored once it was accepted. A selfie is shown
              mirrored, exactly like the live view it came from (Issue #1151) —
              otherwise the picture flips over at the moment of the release.
              Stored is the unmirrored capture, the mirroring is CSS. */}
          <img
            data-testid="kyc-capture-preview"
            src={preview.url}
            alt={t('kyc.capture.camera.previewAlt', 'Vorschau der Aufnahme')}
            className={`w-full rounded-lg border border-gray-200 bg-gray-900 object-contain max-h-72 ${
              isSelfie ? MIRROR_CLASS : ''
            }`}
          />
          {/* Only after a perspective correction — R4: never say it twice */}
          {preview.straightened && (
            <p data-testid="kyc-capture-straightened" role="status" className="text-sm text-gray-500">
              {t('kyc.capture.scanner.straightened', 'Ausweis automatisch gerade gerückt')}
            </p>
          )}
          {!meetsMinResolution(preview.width, preview.height) && (
            <p
              data-testid="kyc-capture-lowres"
              role="status"
              className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
            >
              {t(
                'kyc.capture.camera.lowResolution',
                'Die Aufnahme ist klein ({{width}} × {{height}} px). Gehen Sie näher an den Ausweis oder wählen Sie eine bessere Kamera.',
                { width: preview.width, height: preview.height },
              )}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              data-testid="kyc-capture-retake-btn"
              type="button"
              onClick={handleRetake}
              disabled={busy}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={16} />
              {t('kyc.capture.camera.retake', 'Wiederholen')}
            </button>
            <button
              data-testid="kyc-capture-use-btn"
              type="button"
              onClick={handleUse}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {t('kyc.capture.camera.use', 'Verwenden')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-auto flex-col gap-3">
          {/* overflow-hidden clips the overlay's 9999px shadow to the video area */}
          {/* Two shapes, two behaviours:
              Selfie — a fixed, nearly square box over the full width, because
              face and hand need room side by side (Issue #1150). `shrink-0`
              keeps it at its square: with `flex-auto`/`min-h-0` the flex shrink
              beat `aspect-square` and squeezed the live view to a strip of a few
              dozen pixels on a phone, `object-cover` cutting away everything but
              mouth and chin (Issue #1174). Is the column shorter than that, it
              scrolls — the host sets `overflow-y-auto`.
              Document — the picture takes the height the column has left:
              `flex-auto` grows and shrinks it, `min-h-0` lets it shrink below
              its natural size, so «Aufnehmen» below can never be pushed off the
              screen. `flex-auto` (basis `auto`), not `flex-1` (basis `0`):
              inside a host that does not hand down a height — the desktop dialog
              scrolls instead — a basis of 0 would collapse the picture to
              nothing; with basis `auto` the stream's shape below decides.
              Box and video carry the same shape and the same cap, so whichever
              of the two the browser measures first, they end up the same size —
              the overlays sit on the picture, never next to it. */}
          <div
            className={`relative w-full overflow-hidden rounded-lg ${
              isSelfie ? 'shrink-0 aspect-square max-h-[55dvh]' : 'min-h-0 flex-auto max-h-[60dvh]'
            }`}
            style={isSelfie ? undefined : { aspectRatio: streamAspect ?? FALLBACK_ASPECT_RATIO }}
          >
            <video
              ref={attachVideo}
              aria-label={label}
              muted
              playsInline
              autoPlay
              onLoadedMetadata={handleLoadedMetadata}
              // The stream decides the shape of the live view — a fixed 16:9 box
              // would throw away most of a phone sensor held upright. A selfie is
              // the exception: nearly square, face and hand side by side.
              style={isSelfie ? undefined : { aspectRatio: streamAspect ?? FALLBACK_ASPECT_RATIO }}
              // `h-full w-full object-cover`: the picture fills the box it was
              // given and is cut to it — exactly the cut-out the capture stores.
              // The selfie is mirrored, the overlays above are not (Issue #1151)
              className={`h-full w-full rounded-lg bg-gray-900 object-cover ${
                isSelfie ? `aspect-square max-h-[55dvh] ${MIRROR_CLASS}` : 'max-h-[60dvh]'
              }`}
            />
            {/* Where face and hand go: the oval the detection judges against on
                the left, the hand outline of the finger task on the right — both
                cut out of one darkened frame (Issues #1119, #1150) */}
            {isSelfie && !starting && (
              <svg
                aria-hidden="true"
                viewBox={`0 0 ${GUIDE_VIEW_SIZE} ${GUIDE_VIEW_SIZE}`}
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full pointer-events-none"
              >
                <mask id="kyc-capture-guide-mask">
                  <rect
                    x="0"
                    y="0"
                    width={GUIDE_VIEW_SIZE}
                    height={GUIDE_VIEW_SIZE}
                    fill="white"
                  />
                  {/* Black leaves the picture untouched: face and hand stay bright */}
                  <ellipse {...FACE_OVAL_ELLIPSE} fill="black" />
                  <path d={HAND_PATH} transform={HAND_TRANSFORM} fill="black" />
                </mask>
                <rect
                  x="0"
                  y="0"
                  width={GUIDE_VIEW_SIZE}
                  height={GUIDE_VIEW_SIZE}
                  mask="url(#kyc-capture-guide-mask)"
                  className="fill-black/45"
                />
                {/* `non-scaling-stroke`: the line keeps its width even though the
                    overlay is stretched to a box that is not square */}
                <ellipse
                  data-testid="kyc-capture-face-oval"
                  {...FACE_OVAL_ELLIPSE}
                  fill="none"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  className={faceState === 'ok' ? 'stroke-emerald-400' : 'stroke-white/90'}
                />
                <path
                  data-testid="kyc-capture-hand-guide"
                  d={HAND_PATH}
                  transform={HAND_TRANSFORM}
                  fill="none"
                  strokeWidth={2}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  className="stroke-white/90"
                />
              </svg>
            )}
            {/* How many fingers — in the outline, where the hand is going to be
                (Issue #1150); the countdown above keeps its own place */}
            {isSelfie && !starting && fingerTask !== null && (
              <p
                data-testid="kyc-capture-hand-fingers"
                aria-hidden="true"
                // The digit sits on the undimmed picture — the drop shadow keeps
                // it readable over a bright hand as well
                className="absolute pointer-events-none flex items-center justify-center text-4xl font-bold leading-none text-white tabular-nums drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)] sm:text-5xl"
                style={HAND_LABEL_STYLE}
              >
                {fingerTask}
              </p>
            )}
            {/* The finger task with its countdown, on the picture: that is
                where the customer looks while the seconds run (Issue #1144) */}
            {isSelfie && !starting && countdown !== null && fingerTask !== null && (
              <div
                data-testid="kyc-capture-countdown"
                role="status"
                className="absolute inset-x-0 top-0 flex flex-col items-center gap-0.5 bg-black/60 px-3 py-2 text-center"
              >
                <p className="text-sm font-semibold text-white sm:text-base">
                  {t('kyc.capture.face.showFingersNow', {
                    count: fingerTask,
                    defaultValue: `Zeigen Sie jetzt ${fingerTask} Finger`,
                  })}
                </p>
                {/* The seconds are a picture, not a sentence — a screen reader
                    would otherwise read «3 2 1» over the instruction */}
                <p
                  data-testid="kyc-capture-countdown-seconds"
                  aria-hidden="true"
                  className="text-4xl font-bold leading-none text-white tabular-nums sm:text-5xl"
                >
                  {countdown}
                </p>
              </div>
            )}
            {/* Where the card goes — same rectangle the capture is cut to */}
            {frame && !starting && (
              <div
                data-testid="kyc-capture-frame"
                aria-hidden="true"
                className={`absolute pointer-events-none rounded-lg border-2 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] ${
                  outline?.plausible ? 'border-emerald-400' : 'border-white/90'
                }`}
                style={{
                  left: `${frame.leftPercent}%`,
                  top: `${frame.topPercent}%`,
                  width: `${frame.widthPercent}%`,
                  height: `${frame.heightPercent}%`,
                }}
              />
            )}
            {/* What the scanner sees — the outline sits on the live picture */}
            {outline && !starting && (
              <svg
                data-testid="kyc-capture-scan-outline"
                aria-hidden="true"
                viewBox={`0 0 ${outline.frameWidth} ${outline.frameHeight}`}
                className="absolute inset-0 w-full h-full pointer-events-none"
              >
                <polygon
                  points={outline.quad.map((corner) => `${corner.x},${corner.y}`).join(' ')}
                  strokeWidth={3}
                  className={
                    outline.plausible
                      ? 'fill-emerald-400/10 stroke-emerald-400'
                      : 'fill-transparent stroke-white/70'
                  }
                />
              </svg>
            )}
            {starting && (
              <p className="absolute inset-0 flex items-center justify-center text-sm text-white/80">
                {t('kyc.capture.camera.starting', 'Kamera wird gestartet …')}
              </p>
            )}
          </div>

          {frame && !starting && (
            <p
              data-testid="kyc-capture-frame-hint"
              role="status"
              className="shrink-0 text-sm text-gray-500"
            >
              {scannerHint}
            </p>
          )}

          {/* The reason behind the long wait, not a second version of the line
              above (R4): a bright surface the camera cannot tell the card from
              — plus the way out past the scanner (Issue #1163) */}
          {frame && !starting && backgroundHint && (
            <p
              data-testid="kyc-capture-background-hint"
              role="status"
              className="shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              {t(
                'kyc.capture.scanner.backgroundHint',
                'Tipp: Legen Sie den Ausweis auf eine dunkle, einfarbige Unterlage — oder lösen Sie mit «Aufnehmen» von Hand aus.',
              )}
            </p>
          )}

          {/* One line, one message (R4): what to change about the face right now */}
          {isSelfie && !starting && faceHint && (
            <p
              data-testid="kyc-capture-face-hint"
              role="status"
              className="shrink-0 text-sm text-gray-500"
            >
              {faceHint}
            </p>
          )}

          {devices.length > 1 && (
            <div className="shrink-0">
              <label
                htmlFor="kyc-capture-device"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('kyc.capture.camera.select', 'Kamera')}
              </label>
              <select
                data-testid="kyc-capture-device-select"
                id="kyc-capture-device"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">
                  {facingMode === 'user'
                    ? t('kyc.capture.camera.frontDefault', 'Frontkamera (Standard)')
                    : t('kyc.capture.camera.backDefault', 'Rückkamera (Standard)')}
                </option>
                {devices.map((device, index) => (
                  <option key={device.deviceId || index} value={device.deviceId}>
                    {device.label || t('kyc.capture.camera.numbered', 'Kamera {{n}}', { n: index + 1 })}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            data-testid="kyc-capture-shutter-btn"
            type="button"
            onClick={handleShutter}
            // While the guide is running the picture is taken by itself; the
            // button stays there for the fallback, but not as a way around it.
            disabled={busy || shooting || (faceGuideActive && faceState !== 'ok')}
            // `shrink-0`: the button keeps its height, whatever the picture does
            className="w-full shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {shooting ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
            {t('kyc.capture.camera.shutter', 'Aufnehmen')}
          </button>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  )
}
